import React, { useEffect, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Calendar, Users, MapPin, FileText, Plus, Minus, ShoppingBag, CheckCircle2 } from 'lucide-react';
interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialItem?: string;
}
const eventTypes = ['Wedding', 'Corporate Event', 'Private Party', 'Community Event', 'Meal Prep'];
const cateringPackages = [{
  id: 'p1',
  name: 'Weddings Package',
  type: 'Tiered Pricing',
  description: 'Up to 4 Entrees, 4 Sides, 2 Apps, 4 Hand-crafted Drinks. Luxe decorations included.',
  groups: [{
    range: '10-15 Guests',
    price: '$4.5k - $6.5k',
    numericPrice: 4500
  }, {
    range: '20-40 Guests',
    price: '$6.8k - $8.5k',
    numericPrice: 6800
  }, {
    range: '40-60 Guests',
    price: '$9k - $12.5k',
    numericPrice: 9000
  }, {
    range: '60-100 Guests',
    price: '$15k - $25k',
    numericPrice: 15000
  }]
}, {
  id: 'p2',
  name: 'Luxe Buffet',
  type: 'Tiered Pricing',
  description: 'Up to 3 Entrees, 3 Sides, 1 App. Luxe decorations included.',
  groups: [{
    range: '10-15 Guests',
    price: '$2k - $4k',
    numericPrice: 2000
  }, {
    range: '20-40 Guests',
    price: '$4.5k - $6.8k',
    numericPrice: 4500
  }, {
    range: '40-60 Guests',
    price: '$7.2k - $9.6k',
    numericPrice: 7200
  }, {
    range: '60-100 Guests',
    price: '$9.8k - $15k',
    numericPrice: 9800
  }]
}, {
  id: 'p3',
  name: 'Luxe Canapes & Drinks',
  type: 'Tiered Pricing',
  description: 'Up to 5 Apps, 4 Drinks. Luxe decorations included.',
  groups: [{
    range: '10-15 Guests',
    price: '$1.5k - $2.5k',
    numericPrice: 1500
  }, {
    range: '20-40 Guests',
    price: '$2.8k - $4.8k',
    numericPrice: 2800
  }, {
    range: '40-60 Guests',
    price: '$5k - $7k',
    numericPrice: 5000
  }, {
    range: '60-100 Guests',
    price: '$7.5k - $12k',
    numericPrice: 7500
  }]
}, {
  id: 'p4',
  name: 'Private Chef Experience',
  type: '$200/pp',
  perPersonPrice: 200,
  description: 'Selections from Entree, Brunch, Beverages.'
}, {
  id: 'p5',
  name: 'Corporate - Office Meal',
  type: '$65/pp',
  perPersonPrice: 65,
  description: 'Selections from Entree, Sides, Brunch, Beverages.'
}, {
  id: 'p6',
  name: 'Standard Drop-off Deliver',
  type: '$40/pp',
  perPersonPrice: 40,
  description: 'Selections from Entree, Sides, Brunch.'
}];
const availableAddons = [{
  id: 'v1',
  name: 'Kenfetti Events',
  description: 'Luxury balloon decor and custom event styling for unforgettable celebrations.',
  email: 'kenfettievents@gmail.com',
  phone: '(703)786-1740',
  social: 'Instagram: Kenfettievents_',
  isToggle: true,
  isCustomQuote: true
}, {
  id: 'v2',
  name: 'SimplyPriscilla Photography',
  description: 'Affordable mini sessions, portraits, couples, maternity, newborns, milestones, and more.',
  email: 'simplypriscillaphotography@gmail.com',
  phone: '(301)520-5737',
  social: 'Facebook',
  isToggle: true,
  isCustomQuote: true
}, {
  id: 'v3',
  name: 'LeekVisuals',
  description: 'Providing videos of your special moments that lasts you a lifetime of memorable experiences.',
  email: 'leekvisuals@outlook.com',
  phone: '(240)876-3611',
  social: 'Instagram',
  isToggle: true,
  isCustomQuote: true
}];
export function QuoteModal({
  isOpen,
  onClose,
  initialItem
}: QuoteModalProps) {
  const [step, setStep] = useState(1);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showPricingGuide, setShowPricingGuide] = useState(false);
  const [formData, setFormData] = useState({
    eventType: '',
    attendees: '',
    date: '',
    location: '',
    name: '',
    email: '',
    notes: ''
  });
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Record<string, string>>({});
  const [addonQty, setAddonQty] = useState<Record<string, number>>({});
  const calculateTotal = () => {
    let total = 0;
    const attendees = parseInt(formData.attendees) || 0;
    selectedPackages.forEach(id => {
      const pkg = cateringPackages.find(p => p.id === id);
      if (pkg) {
        if (pkg.groups && selectedGroups[id]) {
          const group = pkg.groups.find(g => g.range === selectedGroups[id]);
          if (group) total += group.numericPrice;
        } else if (pkg.perPersonPrice) {
          total += pkg.perPersonPrice * attendees;
        }
      }
    });
    availableAddons.forEach(addon => {
      const qty = addonQty[addon.id] || 0;
      if (qty > 0 && !addon.isCustomQuote && addon.price) {
        total += addon.price * qty;
      }
    });
    return total;
  };
  useEffect(() => {
    if (isOpen) {
      let mappedType = '';
      if (initialItem) {
        const lowerItem = initialItem.toLowerCase();
        if (lowerItem.includes('wedding')) mappedType = 'Wedding';else if (lowerItem.includes('corporate')) mappedType = 'Corporate Event';else if (lowerItem.includes('meal prep')) mappedType = 'Meal Prep';else if (eventTypes.includes(initialItem)) mappedType = initialItem;
      }
      setFormData({
        eventType: mappedType,
        attendees: '',
        date: '',
        location: '',
        name: '',
        email: '',
        notes: ''
      });
      setStep(1);
      setIsSuccess(false);
      setShowCloseConfirm(false);
      setShowPricingGuide(false);
      setSelectedPackages([]);
      setSelectedGroups({});
      setAddonQty({});
    }
  }, [isOpen, initialItem]);
  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);
  const handleCloseAttempt = () => {
    if (isSuccess) {
      onClose();
      return;
    }
    // Only show confirm if they've started filling things out
    if (formData.eventType || selectedPackages.length > 0) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };
  const togglePackage = (id: string) => {
    setSelectedPackages(prev => {
      const isSelected = prev.includes(id);
      if (isSelected) {
        // Remove group selection if package is deselected
        const newGroups = {
          ...selectedGroups
        };
        delete newGroups[id];
        setSelectedGroups(newGroups);
        return prev.filter(p => p !== id);
      }
      return [...prev, id];
    });
  };
  const selectGroup = (packageId: string, groupRange: string) => {
    if (!selectedPackages.includes(packageId)) {
      setSelectedPackages(prev => [...prev, packageId]);
    }
    setSelectedGroups(prev => ({
      ...prev,
      [packageId]: groupRange
    }));
  };
  const updateQty = (type: 'addon', id: string, delta: number) => {
    setAddonQty(prev => {
      const current = prev[id] || 0;
      const next = Math.max(0, current + delta);
      return {
        ...prev,
        [id]: next
      };
    });
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-249">
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6" data-id="element-250">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleCloseAttempt} className="absolute inset-0 bg-brand-black/80 backdrop-blur-sm" data-id="element-251" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative bg-white w-full max-w-3xl rounded-sm overflow-hidden shadow-2xl flex flex-col max-h-[90vh]" data-id="element-252">
          {/* Close Confirmation Overlay */}
          <AnimatePresence data-id="element-253">
            {showCloseConfirm && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="absolute inset-0 z-[110] bg-brand-black/90 backdrop-blur-md flex items-center justify-center p-8 text-center" data-id="element-254">
                <div className="max-w-sm" data-id="element-255">
                  <h3 className="text-2xl font-serif text-white mb-4" data-id="element-256">
                    Discard Request?
                  </h3>
                  <p className="text-gray-400 mb-8" data-id="element-257">
                    You have an order in progress. Are you sure you want to
                    close? Your selections will be lost.
                  </p>
                  <div className="flex flex-col gap-3" data-id="element-258">
                    <button onClick={() => setShowCloseConfirm(false)} className="w-full bg-brand-gold text-white py-4 rounded-sm font-bold uppercase tracking-widest hover:bg-white hover:text-brand-black transition-all" data-id="element-259">
                      Continue My Order
                    </button>
                    <button onClick={() => {
                  setShowCloseConfirm(false);
                  onClose();
                }} className="w-full border border-white/20 text-white/60 py-4 rounded-sm font-bold uppercase tracking-widest hover:text-white transition-all" data-id="element-260">
                      Yes, Discard
                    </button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Pricing Guide Overlay */}
          <AnimatePresence data-id="element-261">
            {showPricingGuide && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="absolute inset-0 z-[120] bg-brand-black/95 backdrop-blur-md flex flex-col p-4 md:p-8" data-id="element-262">
                <div className="flex justify-between items-center mb-6" data-id="element-263">
                  <h3 className="text-xl md:text-2xl font-serif text-white" data-id="element-264">
                    Catering Style Guide
                  </h3>
                  <button onClick={() => setShowPricingGuide(false)} className="text-white/50 hover:text-white p-2" data-id="element-265">
                    <X size={24} data-id="element-266" />
                  </button>
                </div>
                <div className="flex-grow overflow-auto rounded-sm" data-id="element-267">
                  <img src="https://cdn.magicpatterns.com/uploads/oCxtfmG3XQ3rnHrEkVwDDY/Menu.png" alt="Pricing Guide" className="w-full h-auto" data-id="element-268" />
                </div>
                <button onClick={() => setShowPricingGuide(false)} className="mt-6 w-full bg-brand-gold text-white py-4 rounded-sm font-bold uppercase tracking-widest" data-id="element-269">
                  Back to Selection
                </button>
              </motion.div>}
          </AnimatePresence>

          {/* Header */}
          <div className="bg-brand-black p-6 flex justify-between items-center shrink-0" data-id="element-270">
            <div data-id="element-271">
              <h2 className="text-white font-serif text-2xl" data-id="element-272">
                {isSuccess ? 'Request Received' : 'Catering Order Request'}
              </h2>
              {!isSuccess && <p className="text-brand-gold text-xs uppercase tracking-widest mt-1" data-id="element-273">
                  Step {step} of 4
                </p>}
            </div>
            <button onClick={handleCloseAttempt} className="text-white/50 hover:text-white transition-colors" data-id="element-274">
              <X size={24} data-id="element-275" />
            </button>
          </div>

          {/* Progress Bar */}
          {!isSuccess && <div className="h-1 bg-gray-100 w-full shrink-0" data-id="element-276">
              <motion.div className="h-full bg-brand-gold" initial={{
            width: '25%'
          }} animate={{
            width: `${step / 4 * 100}%`
          }} data-id="element-277" />
            </div>}

          <div className="p-4 md:p-8 overflow-y-auto flex-grow" data-id="element-278">
            {isSuccess ? <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="py-12 text-center" data-id="element-279">
                <div className="w-20 h-20 bg-brand-gold/10 text-brand-gold rounded-full flex items-center justify-center mx-auto mb-8" data-id="element-280">
                  <CheckCircle2 size={48} data-id="element-281" />
                </div>
                <h3 className="text-3xl font-serif mb-4 text-brand-black" data-id="element-282">
                  Thank You, {formData.name.split(' ')[0]}!
                </h3>
                <p className="text-gray-600 max-w-md mx-auto mb-12 leading-relaxed" data-id="element-283">
                  Your catering request has been submitted successfully. Our
                  team will review your details and contact you at{' '}
                  <span className="font-bold text-brand-black" data-id="element-284">
                    {formData.email}
                  </span>{' '}
                  within 24 hours to finalize your event.
                </p>
                <button onClick={onClose} className="bg-brand-black text-white px-12 py-4 rounded-sm font-bold uppercase tracking-widest hover:bg-brand-gold transition-all shadow-xl" data-id="element-285">
                  Back to Website
                </button>
              </motion.div> : <>
                {step === 1 && <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} data-id="element-286">
                    <h3 className="text-xl md:text-2xl mb-6 font-serif" data-id="element-287">
                      Event Details
                    </h3>
                    <div className="space-y-6" data-id="element-288">
                      <div data-id="element-289">
                        <label className="block text-xs md:text-sm font-bold uppercase tracking-widest text-gray-500 mb-3" data-id="element-290">
                          Type of Event
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-291">
                          {eventTypes.map(type => <button key={type} onClick={() => setFormData({
                      ...formData,
                      eventType: type
                    })} className={`p-3 text-sm border rounded-sm transition-all ${formData.eventType === type ? 'border-brand-black bg-brand-black text-white' : 'border-gray-200 hover:border-brand-gold text-gray-700'}`} data-id="element-292">
                              {type}
                            </button>)}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-293">
                        <div className="relative" data-id="element-294">
                          <Users className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-295" />
                          <input type="number" placeholder="Number of Attendees" value={formData.attendees} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-sm focus:border-brand-gold outline-none" onChange={e => setFormData({
                      ...formData,
                      attendees: e.target.value
                    })} data-id="element-296" />
                        </div>
                        <div className="relative" data-id="element-297">
                          <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-298" />
                          <input type="date" value={formData.date} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-sm focus:border-brand-gold outline-none" onChange={e => setFormData({
                      ...formData,
                      date: e.target.value
                    })} data-id="element-299" />
                        </div>
                      </div>
                      <div className="relative" data-id="element-300">
                        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-301" />
                        <input type="text" placeholder="Event Location (DMV Area)" value={formData.location} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-sm focus:border-brand-gold outline-none" onChange={e => setFormData({
                    ...formData,
                    location: e.target.value
                  })} data-id="element-302" />
                      </div>
                    </div>
                  </motion.div>}

                {step === 2 && <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} data-id="element-303">
                    <div className="flex flex-col sm:flex-row justify-between sm:items-end mb-6 gap-4" data-id="element-304">
                      <div data-id="element-305">
                        <h3 className="text-xl md:text-2xl font-serif" data-id="element-306">
                          Select Packages
                        </h3>
                        <p className="text-gray-500 text-sm mt-1" data-id="element-307">
                          Choose the service packages for your{' '}
                          {formData.attendees || '0'} guests.
                        </p>
                      </div>
                      <button onClick={() => setShowPricingGuide(true)} className="text-[10px] font-bold uppercase tracking-widest text-brand-gold border border-brand-gold/30 px-4 py-2 rounded-sm hover:bg-brand-gold hover:text-white transition-all" data-id="element-308">
                        View Pricing Guide
                      </button>
                    </div>

                    <div className="space-y-4" data-id="element-309">
                      {cateringPackages.map(pkg => <div key={pkg.id} className={`p-4 border rounded-sm transition-colors ${selectedPackages.includes(pkg.id) ? 'border-brand-gold bg-brand-gold/5' : 'border-gray-200 hover:border-brand-gold/50'}`} data-id="element-310">
                          <div className="flex flex-col gap-4" data-id="element-311">
                            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 cursor-pointer" onClick={() => togglePackage(pkg.id)} data-id="element-312">
                              <div className="flex-grow" data-id="element-313">
                                <div className="flex items-center gap-3" data-id="element-314">
                                  <div className={`w-5 h-5 rounded-sm border flex items-center justify-center shrink-0 ${selectedPackages.includes(pkg.id) ? 'bg-brand-gold border-brand-gold' : 'border-gray-300'}`} data-id="element-315">
                                    {selectedPackages.includes(pkg.id) && <CheckCircle2 size={14} className="text-white" data-id="element-316" />}
                                  </div>
                                  <h4 className="font-bold text-brand-black" data-id="element-317">
                                    {pkg.name}
                                  </h4>
                                </div>
                                <p className="text-sm text-gray-500 mt-2 ml-8" data-id="element-318">
                                  {pkg.description}
                                </p>
                              </div>
                              <div className="shrink-0 ml-8 sm:ml-0" data-id="element-319">
                                <span className="inline-block text-[10px] font-bold uppercase tracking-widest bg-white border border-gray-200 px-3 py-1.5 rounded-sm text-brand-black" data-id="element-320">
                                  {pkg.type}
                                </span>
                              </div>
                            </div>

                            {pkg.groups && <div className="ml-8 grid grid-cols-2 md:grid-cols-4 gap-2" data-id="element-321">
                                {pkg.groups.map((group, idx) => <button key={idx} onClick={() => selectGroup(pkg.id, group.range)} className={`border p-2 rounded-sm text-center transition-all ${selectedGroups[pkg.id] === group.range ? 'border-brand-gold bg-brand-gold text-white shadow-md' : 'border-gray-100 bg-white/50 hover:border-brand-gold/30'}`} data-id="element-322">
                                    <p className={`text-[9px] uppercase tracking-tighter font-bold ${selectedGroups[pkg.id] === group.range ? 'text-white/80' : 'text-gray-400'}`} data-id="element-323">
                                      {group.range}
                                    </p>
                                    <p className={`text-[11px] font-bold ${selectedGroups[pkg.id] === group.range ? 'text-white' : 'text-brand-black'}`} data-id="element-324">
                                      {group.price}
                                    </p>
                                  </button>)}
                              </div>}
                          </div>
                        </div>)}
                    </div>
                  </motion.div>}

                {step === 3 && <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} data-id="element-325">
                    <h3 className="text-xl md:text-2xl mb-2 font-serif" data-id="element-326">
                      Partner Services
                    </h3>
                    <p className="text-gray-500 text-sm mb-6" data-id="element-327">
                      Enhance your event with our trusted sub-business partners.
                      Select to include them in your inquiry.
                    </p>

                    <div className="space-y-4" data-id="element-328">
                      {availableAddons.map(addon => <div key={addon.id} className={`flex flex-col sm:flex-row justify-between sm:items-center gap-4 p-5 border rounded-sm transition-all ${addonQty[addon.id] ? 'border-brand-gold bg-brand-gold/5' : 'border-gray-200'}`} data-id="element-329">
                          <div className="flex flex-col max-w-lg" data-id="element-330">
                            <span className="font-bold text-brand-black text-lg" data-id="element-331">
                              {addon.name}
                            </span>
                            <span className="text-sm text-gray-500 mt-1 leading-relaxed" data-id="element-332">
                              {addon.description}
                            </span>
                            <div className="mt-3 flex flex-col gap-1 text-xs text-gray-400" data-id="element-333">
                              {addon.email && <span data-id="element-334">Email: {addon.email}</span>}
                              {addon.phone && <span data-id="element-335">Phone: {addon.phone}</span>}
                              {addon.social && <span data-id="element-336">Social: {addon.social}</span>}
                            </div>
                          </div>
                          <div className="shrink-0" data-id="element-337">
                            {addon.isToggle ? <button onClick={() => updateQty('addon', addon.id, addonQty[addon.id] ? -1 : 1)} className={`w-full sm:w-auto px-6 py-3 rounded-sm text-xs font-bold uppercase tracking-widest transition-all ${addonQty[addon.id] ? 'bg-brand-gold text-white shadow-md' : 'bg-white border border-gray-200 text-brand-black hover:border-brand-gold'}`} data-id="element-338">
                                {addonQty[addon.id] ? 'Added to Inquiry' : 'Add to Inquiry'}
                              </button> : <div className="flex items-center justify-between sm:justify-start gap-3 bg-gray-50 rounded-sm p-1 border border-gray-200 w-full sm:w-auto" data-id="element-339">
                                <button onClick={() => updateQty('addon', addon.id, -1)} className="p-2 hover:text-brand-gold transition-colors" data-id="element-340">
                                  <Minus size={16} data-id="element-341" />
                                </button>
                                <span className="w-8 text-center font-bold" data-id="element-342">
                                  {addonQty[addon.id] || 0}
                                </span>
                                <button onClick={() => updateQty('addon', addon.id, 1)} className="p-2 hover:text-brand-gold transition-colors" data-id="element-343">
                                  <Plus size={16} data-id="element-344" />
                                </button>
                              </div>}
                          </div>
                        </div>)}
                    </div>
                  </motion.div>}

                {step === 4 && <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} data-id="element-345">
                    <h3 className="text-xl md:text-2xl mb-6 font-serif" data-id="element-346">
                      Review & Checkout
                    </h3>

                    <div className="bg-gray-50 p-6 rounded-sm border border-gray-200 mb-8" data-id="element-347">
                      <h4 className="font-bold uppercase tracking-widest text-xs text-gray-500 mb-4 border-b border-gray-200 pb-2" data-id="element-348">
                        Order Summary
                      </h4>

                      <div className="mb-6" data-id="element-349">
                        <p className="text-sm" data-id="element-350">
                          <span className="font-bold" data-id="element-351">Event:</span>{' '}
                          {formData.eventType} ({formData.attendees} Guests)
                        </p>
                        <p className="text-sm" data-id="element-352">
                          <span className="font-bold" data-id="element-353">Date:</span>{' '}
                          {formData.date || 'TBD'} |{' '}
                          <span className="font-bold" data-id="element-354">Location:</span>{' '}
                          {formData.location || 'TBD'}
                        </p>
                      </div>

                      <div className="space-y-3 mb-6" data-id="element-355">
                        <p className="font-bold text-sm border-b border-gray-100 pb-1" data-id="element-356">
                          Selected Packages:
                        </p>
                        {cateringPackages.filter(p => selectedPackages.includes(p.id)).map(p => {
                    let price = 0;
                    if (p.groups && selectedGroups[p.id]) {
                      price = p.groups.find(g => g.range === selectedGroups[p.id])?.numericPrice || 0;
                    } else if (p.perPersonPrice) {
                      price = p.perPersonPrice * (parseInt(formData.attendees) || 0);
                    }
                    return <div key={p.id} className="flex justify-between text-sm" data-id="element-357">
                                <div className="text-gray-600" data-id="element-358">
                                  <span className="font-medium text-brand-black" data-id="element-359">
                                    • {p.name}
                                  </span>
                                  {selectedGroups[p.id] && <span className="text-brand-gold font-bold ml-1" data-id="element-360">
                                      ({selectedGroups[p.id]})
                                    </span>}
                                </div>
                                <span className="font-bold text-brand-black" data-id="element-361">
                                  ${price.toLocaleString()}
                                </span>
                              </div>;
                  })}
                        {selectedPackages.length === 0 && <p className="text-sm text-red-500 italic" data-id="element-362">
                            No packages selected.
                          </p>}
                      </div>

                      {Object.values(addonQty).some(v => v > 0) && <div className="space-y-3 mb-6" data-id="element-363">
                          <p className="font-bold text-sm border-b border-gray-100 pb-1" data-id="element-364">
                            Partner Services (Custom Quote):
                          </p>
                          {availableAddons.filter(a => addonQty[a.id]).map(a => <div key={a.id} className="flex justify-between text-sm" data-id="element-365">
                                <span className="text-gray-600 font-medium" data-id="element-366">
                                  • {a.name}
                                </span>
                                <span className="font-bold text-brand-gold text-xs uppercase tracking-widest" data-id="element-367">
                                  Inquiry Added
                                </span>
                              </div>)}
                        </div>}

                      <div className="border-t-2 border-brand-gold pt-4 flex justify-between items-center" data-id="element-368">
                        <span className="text-lg font-serif font-bold text-brand-black" data-id="element-369">
                          Estimated Subtotal
                        </span>
                        <span className="text-2xl font-serif font-bold text-brand-gold" data-id="element-370">
                          ${calculateTotal().toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-4" data-id="element-371">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-372">
                        <input type="text" placeholder="Full Name" className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:border-brand-gold outline-none" onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} data-id="element-373" />
                        <input type="email" placeholder="Email Address" className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:border-brand-gold outline-none" onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} data-id="element-374" />
                      </div>
                      <textarea placeholder="Any special requests, dietary restrictions, or notes?" className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:border-brand-gold outline-none h-24 resize-none" onChange={e => setFormData({
                  ...formData,
                  notes: e.target.value
                })} data-id="element-375" />
                    </div>
                  </motion.div>}
              </>}
          </div>

          {/* Footer */}
          {!isSuccess && <div className="p-6 border-t border-gray-100 flex justify-between bg-gray-50 shrink-0" data-id="element-376">
              {step > 1 ? <button onClick={prevStep} className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-gray-500 hover:text-brand-black transition-colors" data-id="element-377">
                  <ChevronLeft size={18} data-id="element-378" /> Back
                </button> : <div data-id="element-379" />}

              {step < 4 ? <button onClick={nextStep} disabled={step === 1 && (!formData.eventType || !formData.attendees) || step === 2 && (selectedPackages.length === 0 || selectedPackages.some(id => cateringPackages.find(p => p.id === id)?.groups && !selectedGroups[id]))} className="bg-brand-black text-white px-8 py-3 rounded-sm text-xs font-bold uppercase tracking-widest hover:bg-brand-gold transition-all disabled:opacity-50 flex items-center gap-2" data-id="element-380">
                  Next Step <ChevronRight size={18} data-id="element-381" />
                </button> : <button onClick={() => setIsSuccess(true)} className="bg-brand-gold text-white px-10 py-3 rounded-sm text-xs font-bold uppercase tracking-widest hover:bg-brand-black transition-all flex items-center gap-2" data-id="element-382">
                  <ShoppingBag size={18} data-id="element-383" /> Submit Order
                </button>}
            </div>}
        </motion.div>
      </div>
    </AnimatePresence>;
}